import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { supportService } from '../services/support.service';
import type {
  AdminSupportQueue,
  SupportTicketStatus,
} from '../types/support.types';

const SUPPORT_EMAIL_DELIVERY_TOAST_ID = 'support-email-delivery';

export const supportKeys = {
  all: ['support'] as const,
  tickets: () => ['support', 'tickets'] as const,
  ticketList: (page: number) => ['support', 'tickets', page] as const,
  ticket: (uuid: string) => ['support', 'ticket', uuid] as const,
  admin: () => ['support', 'admin'] as const,
  adminLists: () => ['support', 'admin', 'tickets'] as const,
  adminList: (queue: AdminSupportQueue, page: number) =>
    ['support', 'admin', 'tickets', queue, page] as const,
  adminTicket: (uuid: string) => ['support', 'admin', 'ticket', uuid] as const,
  adminStats: () => ['support', 'admin', 'stats'] as const,
};

export function useSupportTickets(page: number) {
  return useQuery({
    queryKey: supportKeys.ticketList(page),
    queryFn: () => supportService.getTickets(page),
    staleTime: 30_000,
  });
}

export function useSupportTicket(uuid: string | null) {
  return useQuery({
    queryKey: supportKeys.ticket(uuid ?? ''),
    queryFn: () => supportService.getTicket(uuid!),
    enabled: Boolean(uuid),
    staleTime: 30_000,
  });
}

export function useSupportReply(ticketUuid: string | null) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (content: string) =>
      supportService.reply(ticketUuid!, { content }),
    onSuccess: () => {
      if (ticketUuid) {
        queryClient.invalidateQueries({
          queryKey: supportKeys.ticket(ticketUuid),
        });
      }
      queryClient.invalidateQueries({ queryKey: supportKeys.tickets() });
      toast.success(
        t('support.replySuccess', 'Votre message a été ajouté à la demande')
      );
    },
    onError: (error: HttpApiError) => {
      console.error('Support reply error:', error);
      toast.error(
        t(
          'support.replyError',
          "Impossible d'ajouter votre message. Veuillez réessayer."
        )
      );
    },
  });
}

export function useResolveSupportTicket(ticketUuid: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => supportService.resolveTicket(ticketUuid!),
    onSuccess: () => {
      if (ticketUuid) {
        queryClient.invalidateQueries({
          queryKey: supportKeys.ticket(ticketUuid),
        });
      }
      queryClient.invalidateQueries({ queryKey: supportKeys.tickets() });
      toast.success('La demande est marquée comme résolue');
    },
    onError: () =>
      toast.error('Impossible de résoudre la demande. Veuillez réessayer.'),
  });
}

export function useAdminSupportTickets(queue: AdminSupportQueue, page: number) {
  return useQuery({
    queryKey: supportKeys.adminList(queue, page),
    queryFn: () => supportService.getAdminTickets(queue, page),
    staleTime: 15_000,
  });
}

export function useAdminSupportStats() {
  return useQuery({
    queryKey: supportKeys.adminStats(),
    queryFn: () => supportService.getAdminStats(),
    staleTime: 15_000,
  });
}

export function useAdminSupportTicket(uuid: string | null) {
  return useQuery({
    queryKey: supportKeys.adminTicket(uuid ?? ''),
    queryFn: () => supportService.getAdminTicket(uuid!),
    enabled: Boolean(uuid),
    staleTime: 15_000,
  });
}

export function useAdminSupportReply(ticketUuid: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (content: string) =>
      supportService.adminReply(ticketUuid, content),
    onSuccess: ticket => {
      queryClient.setQueryData(supportKeys.adminTicket(ticketUuid), ticket);
      queryClient.invalidateQueries({ queryKey: supportKeys.adminLists() });
      queryClient.invalidateQueries({ queryKey: supportKeys.adminStats() });
      const lastReply = ticket.replies[ticket.replies.length - 1];
      if (lastReply?.emailDeliveryStatus === 'failed') {
        toast.error(
          "La réponse est enregistrée, mais l'e-mail n'a pas été envoyé. Relancez-le depuis le fil.",
          { id: SUPPORT_EMAIL_DELIVERY_TOAST_ID }
        );
      } else {
        toast.success('Réponse enregistrée et notification envoyée', {
          id: SUPPORT_EMAIL_DELIVERY_TOAST_ID,
        });
      }
    },
    onError: () =>
      toast.error("Impossible d'enregistrer la réponse. Veuillez réessayer."),
  });
}

export function useRetryAdminSupportEmail(
  ticketUuid: string,
  replyUuid: string
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      supportService.retryAdminReplyEmail(ticketUuid, replyUuid),
    onSuccess: ticket => {
      queryClient.setQueryData(supportKeys.adminTicket(ticketUuid), ticket);
      queryClient.invalidateQueries({ queryKey: supportKeys.adminLists() });
      queryClient.invalidateQueries({ queryKey: supportKeys.adminStats() });
      const retriedReply = ticket.replies.find(item => item.uuid === replyUuid);
      if (retriedReply?.emailDeliveryStatus === 'sent') {
        toast.success('Notification e-mail envoyée', {
          id: SUPPORT_EMAIL_DELIVERY_TOAST_ID,
        });
      } else {
        toast.error("L'e-mail n'est toujours pas parti. Réessayez plus tard.", {
          id: SUPPORT_EMAIL_DELIVERY_TOAST_ID,
        });
      }
    },
    onError: () =>
      toast.error("Impossible de relancer l'e-mail.", {
        id: SUPPORT_EMAIL_DELIVERY_TOAST_ID,
      }),
  });
}

export function useCloseAdminSupportTicket(ticketUuid: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (source: 'admin_confirmed' | 'external_email') =>
      supportService.closeAdminTicket(ticketUuid, source),
    onSuccess: ticket => {
      queryClient.setQueryData(supportKeys.adminTicket(ticketUuid), ticket);
      queryClient.invalidateQueries({ queryKey: supportKeys.adminLists() });
      queryClient.invalidateQueries({ queryKey: supportKeys.adminStats() });
      toast.success('La demande est clôturée', {
        id: SUPPORT_EMAIL_DELIVERY_TOAST_ID,
      });
    },
    onError: () => toast.error('Impossible de clôturer la demande.'),
  });
}

export function useUpdateAdminSupportStatus(ticketUuid: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (status: SupportTicketStatus) =>
      supportService.updateAdminTicketStatus(ticketUuid, status),
    onSuccess: ticket => {
      queryClient.setQueryData(supportKeys.adminTicket(ticketUuid), ticket);
      queryClient.invalidateQueries({ queryKey: supportKeys.adminLists() });
      queryClient.invalidateQueries({ queryKey: supportKeys.adminStats() });
      toast.success('La demande est de nouveau à traiter');
    },
    onError: () => toast.error('Impossible de rouvrir la demande.'),
  });
}
